import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/useAuth';
import { useDeleteUser } from '@/features/users';
import { useNotifications } from '@/providers/notificationHooks';
import { ApiError } from '@/shared/data/client';

export type DeleteAccountPanelState = {
  deleteOpen: boolean;
  deletePassword: string;
  deletePasswordError: string | null;
  deleteDisabled: boolean;
  confirmDisabled: boolean;
  isDeleting: boolean;
  onOpen: () => void;
  onClose: () => void;
  onPasswordChange: (value: string) => void;
  onConfirm: () => void;
};

export const useDeleteAccountPanel = (): DeleteAccountPanelState => {
  const { setSessionDirect, session: authSession, signOut } = useAuth();
  const navigate = useNavigate();
  const deleteUser = useDeleteUser();
  const { notify } = useNotifications();
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePassword, setDeletePassword] = useState('');
  const [deletePasswordError, setDeletePasswordError] = useState<string | null>(null);

  const deleteMatches = deletePassword.trim().length > 0;
  const deleteDisabled = deleteUser.isPending || !authSession;
  const confirmDisabled = !deleteMatches || !authSession;

  const handleOpenDelete = (): void => {
    if (!authSession) return;
    setDeletePasswordError(null);
    setDeletePassword('');
    setDeleteOpen(true);
  };

  const handleCloseDelete = (): void => {
    setDeleteOpen(false);
    setDeletePasswordError(null);
  };

  const handleConfirmDelete = (): void => {
    if (!authSession || !deleteMatches) return;
    deleteUser.mutate(
      { id: authSession.userId, password: deletePassword },
      {
        onSuccess: () => {
          setDeleteOpen(false);
          notify({ message: 'Account deleted', tone: 'blue' });
          setSessionDirect(null);
          signOut();
          void navigate('/');
          window.location.reload();
        },
        onError: (err) => {
          const apiError = err instanceof ApiError ? err : null;
          if (apiError?.code === 'FORBIDDEN') {
            setDeletePasswordError('Incorrect password');
            return;
          }
          setDeletePasswordError('Unable to delete account. Try again.');
        },
      },
    );
  };

  return {
    deleteOpen,
    deletePassword,
    deletePasswordError,
    deleteDisabled,
    confirmDisabled,
    isDeleting: deleteUser.isPending,
    onOpen: handleOpenDelete,
    onClose: handleCloseDelete,
    onPasswordChange: (value) => {
      setDeletePassword(value);
      setDeletePasswordError(null);
    },
    onConfirm: handleConfirmDelete,
  };
};
